import { Component, Object3DSetEvent, registerComponent, shaders } from "aframe";
import { Camera, ClampToEdgeWrapping, LinearFilter, LinearMipMapLinearFilter, Matrix4, Mesh, PerspectiveCamera, Plane, RGBAFormat, Scene, ShaderMaterial, UniformsUtils, Vector3, Vector3Like, Vector4, WebGLRenderer, WebGLRenderTarget, WebGLRenderTargetOptions } from "three";
import { transformVector } from "../three/math";
import { LayersSystem } from "./layers";

const shader = 
{
	uniforms: 
	{
		'tDiffuse': { value: null },
		'textureMatrix': { value: null }
	},

	vertexShader: [
		'uniform mat4 textureMatrix;',
		'varying vec4 vUv;',
		'void main() {',
		'	vUv = textureMatrix * vec4( position, 1.0 );',
		'	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );',
		'}'
	].join( '\n' ),

	fragmentShader: [
		'uniform sampler2D tDiffuse;',
		'varying vec4 vUv;',
		'void main() {',
		'gl_FragColor = vec4( pow( texture2DProj( tDiffuse, vUv ).rgb, vec3(0.45) ), 1.0 );',
		'}'
	].join( '\n' )
};




interface MirrorComponent extends Component
{
	data : 
	{
		textureSize : {x : number, y : number };
		clipBias : number;
		mipmaps : boolean;
		layers : string[];
		axis : Vector3Like;
		doubleSide : boolean;
	};
	layers : number;
	renderTarget : WebGLRenderTarget;
	material : ShaderMaterial;
	textureMatrix : Matrix4;
}


//modified from js mirror example: https://github.com/mrdoob/js/blob/r125/examples/jsm/objects/Reflector.js 
const setupMirror = ( mirror : MirrorComponent, mesh : Mesh )  =>
{
	//
	var reflectorPlane = new Plane();
	var normal = new Vector3();
	var reflectorWorldPosition = new Vector3();
	var cameraWorldPosition = new Vector3();
	var lookAtPosition = new Vector3( 0, 0, - 1 );
	var clipPlane = new Vector4();

	var view = new Vector3();
	var target = new Vector3();
	var q = new Vector4();
	const viewport = new Vector4();

	var virtualCamera = new PerspectiveCamera();

	mesh.material = mirror.material;
	mesh.onBeforeRender = ( renderer : WebGLRenderer, scene : Scene, camera : Camera ) =>
	{
		reflectorWorldPosition.setFromMatrixPosition( mesh.matrixWorld );
		cameraWorldPosition.setFromMatrixPosition( camera.matrixWorld );
		view.subVectors( reflectorWorldPosition, cameraWorldPosition );

		//normal.fromArray( mesh.matrixWorld.elements, 8 );
		transformVector( normal.copy(mirror.data.axis), mesh.matrixWorld.elements );

		// Avoid rendering when reflector is facing away
		if ( view.dot( normal ) > 0 )
		{
			if(mirror.data.doubleSide)
				normal.negate();
			else
				return;
		} 

		normal.normalize();

		view.reflect( normal ).negate().add( reflectorWorldPosition );

		lookAtPosition.fromArray( camera.matrixWorld.elements, 8 ).negate().add( cameraWorldPosition );

		

		target.subVectors( reflectorWorldPosition, lookAtPosition ).reflect( normal ).negate().add( reflectorWorldPosition );

		virtualCamera.layers.mask = mirror.layers;
		
		virtualCamera.position.copy( view );
		virtualCamera.up.fromArray( camera.matrixWorld.elements, 4 ).reflect( normal );
		virtualCamera.lookAt( target );

		virtualCamera.far = (camera as any).far; // Used in WebGLBackground

		virtualCamera.updateMatrixWorld();
		virtualCamera.projectionMatrix.copy( camera.projectionMatrix );

		// Update the texture matrix
		const textureMatrix = mirror.textureMatrix;
		textureMatrix.set(
			0.5, 0.0, 0.0, 0.5,
			0.0, 0.5, 0.0, 0.5,
			0.0, 0.0, 0.5, 0.5,
			0.0, 0.0, 0.0, 1.0
		);
		//meshMatrix.extractRotation(mesh.matrixWorld);
		//meshMatrix.copyPosition(mesh.matrixWorld);

		textureMatrix.multiply( virtualCamera.projectionMatrix ).multiply( virtualCamera.matrixWorldInverse ).multiply( mesh.matrixWorld );

		// Now update projection matrix with new clip plane, implementing code from: http://www.terathon.com/code/oblique.html
		// Paper explaining this technique: http://www.terathon.com/lengyel/Lengyel-Oblique.pdf
		reflectorPlane.setFromNormalAndCoplanarPoint( normal, reflectorWorldPosition );
		reflectorPlane.applyMatrix4( virtualCamera.matrixWorldInverse );

		clipPlane.set( reflectorPlane.normal.x, reflectorPlane.normal.y, reflectorPlane.normal.z, reflectorPlane.constant );

		var projectionMatrix = virtualCamera.projectionMatrix;

		q.x = ( Math.sign( clipPlane.x ) + projectionMatrix.elements[ 8 ] ) / projectionMatrix.elements[ 0 ];
		q.y = ( Math.sign( clipPlane.y ) + projectionMatrix.elements[ 9 ] ) / projectionMatrix.elements[ 5 ];
		q.z = - 1.0;
		q.w = ( 1.0 + projectionMatrix.elements[ 10 ] ) / projectionMatrix.elements[ 14 ];

		// Calculate the scaled plane vector
		clipPlane.multiplyScalar( 2.0 / clipPlane.dot( q ) );

		// Replacing the third row of the projection matrix
		projectionMatrix.elements[ 2 ] = clipPlane.x;
		projectionMatrix.elements[ 6 ] = clipPlane.y;
		projectionMatrix.elements[ 10 ] = clipPlane.z + 1.0 - mirror.data.clipBias;
		projectionMatrix.elements[ 14 ] = clipPlane.w;

		var currentRenderTarget = renderer.getRenderTarget();
		var currentXrEnabled = renderer.xr.enabled;
		var currentShadowAutoUpdate = renderer.shadowMap.autoUpdate;
		renderer.getCurrentViewport(viewport);

		renderer.setRenderTarget( mirror.renderTarget );
		renderer.xr.enabled = false; // Avoid camera modification
		renderer.shadowMap.autoUpdate = false; // Avoid re-computing shadows

		renderer.state.buffers.depth.setMask( true ); // make sure the depth buffer is writable so it can be properly cleared, see #18897
		renderer.autoClear || renderer.clear();
  
		var currentVisible = mesh.visible; // Probably true...
		mesh.visible = false;
		renderer.render( scene, virtualCamera );
		mesh.visible = currentVisible;
    
		renderer.setRenderTarget( currentRenderTarget );
		renderer.state.viewport(viewport);
		renderer.xr.enabled = currentXrEnabled;
		renderer.shadowMap.autoUpdate = currentShadowAutoUpdate;
	};
};


registerComponent('mirror', 
{
	schema : {
		//textureWidth: 	{type:'int', 		default:512},
		//textureHeight: 	{type:'int', 		default:512},
		textureSize : { type : 'vec2', default : { x:1024, y:1024 } },
		clipBias: 		  {type:'number', default:0.0},
		mipmaps : { type : 'boolean', default : false },
		layers: 	{ type:"array", default:["Default"] },
		axis : { type : 'vec3', default : { x:0, y:0, z:1 }},
		doubleSide : { type : "boolean", default : false }
	},
	
	init(this: MirrorComponent) 
	{
		//this.el.object3DMap.mesh
		this.textureMatrix = new Matrix4();
		this.layers = 0;

		this.material = new ShaderMaterial( {
			uniforms: UniformsUtils.clone( shader.uniforms ),
			fragmentShader: shader.fragmentShader,
			vertexShader: shader.vertexShader
		} );
		this.material.uniforms[ 'textureMatrix' ].value = this.textureMatrix;


	
		if(this.el.object3D instanceof Mesh)
			return setupMirror(this, this.el.object3D as Mesh);
		
		for(let key in this.el.object3DMap)
		{
			const obj = this.el.object3DMap[key];
			if(obj instanceof Mesh)
				return setupMirror(this, obj as Mesh);
		}
		
		const objectSetListener = (event : Object3DSetEvent) => {
			if(event.detail.object instanceof Mesh)
			{
				setupMirror(this, event.detail.object as Mesh);
				this.el.removeEventListener("object3dset", objectSetListener as any);
			}
		};
		this.el.addEventListener("object3dset", objectSetListener );
	},
	
	update(this: MirrorComponent)
	{
		const layersSystem = this.el.sceneEl!.systems.layers as LayersSystem;
		this.layers = layersSystem.maskOf(this.data.layers);

		const parameters : WebGLRenderTargetOptions = {
			minFilter: this.data.mipmaps ? LinearMipMapLinearFilter : LinearFilter,
			magFilter: LinearFilter,
			generateMipmaps: this.data.mipmaps,
			format: RGBAFormat,
			wrapS: ClampToEdgeWrapping,
			wrapT: ClampToEdgeWrapping
		};
		this.renderTarget = new WebGLRenderTarget( this.data.textureSize.x, this.data.textureSize.y, parameters );
		this.material.uniforms[ 'tDiffuse' ].value = this.renderTarget.texture;
	}
});